import { Trans, useLingui } from '@lingui/react/macro';

import {
  GoToCommentsButton,
  GoToCommentsMenuItem,
} from '@/components/comments/GoToCommentsButton/GoToCommentsButton';
import Button from '@/design_system/Button/Button';
import Menu from '@/design_system/Menu';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import IconArrow from '@/icons/Arrow.svg';
import IconMore from '@/icons/More.svg';
import {
  canBeCreatedBy,
  ShipmentWithRelations,
  useCarrierName,
  useComments,
} from '@/models/shipment';
import {
  EditShipmentButton,
  EditShipmentMenuItem,
  useEditShipment,
} from '@/routes/Shipments/Shipment/components/ShipmentHeader/EditShipmentButton';
import {
  PrintItemsSheetsButton,
  PrintItemsSheetsMenuItem,
  PrintPackingListMenuItem,
  PrintShippingLabelButton,
  PrintShippingLabelMenuItem,
} from '@/routes/Shipments/Shipment/components/ShipmentHeader/PrintsButton';
import { useCurrentSession } from '@/services/auth';
import useViewPort from '@/utils/useViewport';

export const ShipmentHeader = ({
  shipment,
  onCommentButtonPress,
}: {
  shipment: ShipmentWithRelations;
  onCommentButtonPress: () => void;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const { currentSession } = useCurrentSession();
  const { data: { comments } = { comments: [] } } = useComments({
    shipmentId: shipment.id,
  });

  const canEditShipment = canBeCreatedBy(shipment, currentSession) && shipment.status === 'draft';

  const { editShipment } = useEditShipment({ shipment });

  let carrier = useCarrierName(shipment);
  const isPrivateCarrier = shipment?.carrier === 'private-carrier';

  if (isPrivateCarrier) {
    carrier = t({ id: 'shipment.header.carrier.private', message: 'private carrier' });
  }

  return (
    <>
      {isMobile && (
        <Button variant="secondary" size="large" to="/shipments" iconOnly noBorder>
          <IconArrow left />
        </Button>
      )}

      <Title size={isMobile ? '500' : '300'} weight={isMobile ? 'medium' : 'bold'} as="h2" flex={1}>
        [{shipment.reference}]{' '}
        <span style={{ textTransform: isMobile ? 'capitalize' : undefined }}>
          {isMobile ? carrier : <Trans id="shipment.header.carrier">Shipment via {carrier}</Trans>}
        </span>
      </Title>

      {!isMobile && (
        <Stack row gap="0.5rem">
          {canEditShipment && <EditShipmentButton onPress={editShipment} />}
          <GoToCommentsButton
            onCommentButtonPress={onCommentButtonPress}
            nbOfComments={comments.length}
          />
          {shipment.status !== 'draft' && <PrintShippingLabelButton shipment={shipment} />}
          {shipment.step === 'preparation' && <PrintItemsSheetsButton shipment={shipment} />}
        </Stack>
      )}
      {isMobile && (
        <Menu
          placement="bottom end"
          trigger={
            <Button variant="secondary" size="large" iconOnly noBorder>
              <IconMore />
            </Button>
          }
          onAction={(id) => {
            switch (id) {
              case 'edit-shipment':
                editShipment();
                break;
              case 'go-to-comments':
                onCommentButtonPress();
                break;
              default:
                break;
            }
          }}
        >
          {canEditShipment && <EditShipmentMenuItem />}
          <GoToCommentsMenuItem nbOfComments={comments.length} />
          {shipment.status !== 'draft' && <PrintShippingLabelMenuItem shipment={shipment} />}
          {shipment.status !== 'draft' ? <PrintPackingListMenuItem shipment={shipment} /> : null}
          {shipment.step === 'preparation' && <PrintItemsSheetsMenuItem shipment={shipment} />}
        </Menu>
      )}
    </>
  );
};
